import moment from 'moment';
import { fyStartMonth } from './financialYear';

/**
 * The arithmetic behind the comparison view.
 *
 * Two groups are read over two windows that are only sometimes the same one:
 * comparing locations or measures reads both over one window, and comparing
 * financial years reads each over its own, because a window anchored to today
 * matches nothing in a year that does not contain today. Everything that
 * follows from that — how two series share one axis, what the totals are, how
 * the breakdowns merge — is here as pure functions, so the shape of a
 * comparison can be tested without a chart or a network around it.
 */

/**
 * A group's total across the whole window.
 * @param {Array<object>} rows - Widget rows carrying `sum_value`.
 * @returns {number} The sum, 0 when nothing reported.
 */
export const totalOf = (rows = []) =>
  (Array.isArray(rows) ? rows : []).reduce(
    (sum, row) => sum + (Number(row?.sum_value) || 0),
    0
  );

/**
 * Collapses a group's rows to one value per period.
 *
 * A measure requested with stacking returns a row per series per period, and
 * the comparison draws one line per group rather than one per series, so the
 * series are summed back together here.
 *
 * @param {Array<object>} rows - Widget rows carrying `period_ts` and `sum_value`.
 * @returns {Array<{ts: string, value: number}>} Periods, oldest first.
 */
export const perPeriod = (rows = []) => {
  const totals = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row?.period_ts) return;

    totals.set(row.period_ts, (totals.get(row.period_ts) || 0) + (Number(row.sum_value) || 0));
  });

  return Array.from(totals.entries())
    .map(([ts, value]) => ({ ts, value }))
    .sort((a, b) => new Date(a.ts) - new Date(b.ts));
};

/** Months in a financial year, and so slots on a year-against-year axis. */
const MONTHS_IN_YEAR = 12;

/**
 * The first day of a financial year, from its own value.
 * @param {string} financialYear - Value such as '2025-2026'.
 * @returns {object|null} A moment at the month this company's year opens in,
 *   or null if unparseable.
 */
const startOfFinancialYear = (financialYear) => {
  const match = /^(\d{4})\s*-\s*\d{4}$/.exec(String(financialYear || '').trim());
  if (!match) return null;

  return moment().year(Number(match[1])).month(fyStartMonth()).startOf('month');
};

/**
 * How many months into its financial year a period falls.
 *
 * This is what lets two years share an axis. Aligning them by position instead
 * only works while both are complete: 2025-2026 first reported in August, so
 * its first row is August and the other year's first row is April, and pairing
 * them by index drew August against April and every later month one place out.
 *
 * @param {string} ts - The period's timestamp.
 * @param {string} financialYear - The year the period belongs to.
 * @returns {number|null} 0 for the month the year opens in through 11 for the
 *   month it closes in, or null when either
 *   value is unusable.
 */
export const monthOfFinancialYear = (ts, financialYear) => {
  const start = startOfFinancialYear(financialYear);
  const at = ts ? moment(ts) : null;
  if (!start || !at?.isValid()) return null;

  /* A whole-month diff truncates, so the day before the year opens measures as
     zero months and would be read as the opening month. The boundary is
     checked directly. */
  if (at.isBefore(start)) return null;

  const offset = at.diff(start, 'months');
  return offset < MONTHS_IN_YEAR ? offset : null;
};

/**
 * The twelve month labels of a financial year, in its own order.
 *
 * The month is read once rather than per label: this builds twelve of them,
 * and the answer cannot change between the first and the twelfth.
 *
 * @returns {Array<string>} Twelve months from the one the year opens in.
 */
export const financialYearMonths = () => {
  const start = fyStartMonth();

  return Array.from({ length: MONTHS_IN_YEAR }, (_, index) =>
    moment().month(start).add(index, 'months').format('MMM')
  );
};

/**
 * Lays both groups over one axis, in the row shape the chart already reads.
 *
 * Comparing two financial years reads each group over its own dates, so the
 * series share no timestamps and plotting them literally would set them side
 * by side rather than on top of each other. Given both years, each row is
 * placed by how many months into its own year it falls, so a month sits above
 * the same month whichever calendar year it belongs to and the axis runs from
 * the month the company's year opens in
 * rather than running across fifteen months of two different years.
 *
 * Without both years — comparing locations or measures, which read one year —
 * the timestamps already line up and are used as they are.
 *
 * Both groups get a row at every position even where one has no reading. That
 * fixes which series is drawn first and so which colour each is given — a
 * group that happened to start reporting later would otherwise take the
 * other's colour and swap the meaning of the swatches on the cards beside the
 * chart. A position with no reading carries no value rather than a zero, so
 * the chart breaks its line there instead of drawing a drop to the floor.
 *
 * @param {Array<object>} rowsA - First group's widget rows.
 * @param {Array<object>} rowsB - Second group's widget rows.
 * @param {string} labelA - First group's name.
 * @param {string} labelB - Second group's name.
 * @param {{a: string, b: string}} years - Each group's financial year, when
 *   the two differ. Omitted for a comparison that reads one year.
 * @returns {Array<object>} Widget rows, two per aligned position.
 */
export const overlayRows = (rowsA = [], rowsB = [], labelA, labelB, years = null) => {
  const seriesA = perPeriod(rowsA);
  const seriesB = perPeriod(rowsB);

  if (years?.a && years?.b) {
    return overlayByMonth(seriesA, seriesB, labelA, labelB, years);
  }

  const stamps = seriesA.length >= seriesB.length
    ? seriesA.map((point) => point.ts)
    : seriesB.map((point) => point.ts);

  return stamps.flatMap((ts, index) => [
    {
      period_ts: ts,
      displayPeriods: ts,
      kpi: labelA,
      sum_value: seriesA[index] ? seriesA[index].value : null,
    },
    {
      period_ts: ts,
      displayPeriods: ts,
      kpi: labelB,
      sum_value: seriesB[index] ? seriesB[index].value : null,
    },
  ]);
};

/**
 * Overlays two financial years on one Apr-to-Mar frame.
 *
 * Every month gets a slot whether or not either year reported in it, so a year
 * that started late leaves a gap where the missing months are instead of
 * sliding its readings back to the start of the axis.
 *
 * @param {Array<{ts: string, value: number}>} seriesA - First group's periods.
 * @param {Array<{ts: string, value: number}>} seriesB - Second group's periods.
 * @param {string} labelA - First group's name.
 * @param {string} labelB - Second group's name.
 * @param {{a: string, b: string}} years - Each group's financial year.
 * @returns {Array<object>} Widget rows, two per month.
 */
const overlayByMonth = (seriesA, seriesB, labelA, labelB, years) => {
  const intoSlots = (series, financialYear) => {
    const slots = new Array(MONTHS_IN_YEAR).fill(null);

    series.forEach(({ ts, value }) => {
      const month = monthOfFinancialYear(ts, financialYear);
      if (month === null) return;

      slots[month] = (slots[month] || 0) + value;
    });

    return slots;
  };

  const a = intoSlots(seriesA, years.a);
  const b = intoSlots(seriesB, years.b);

  /* Twelve empty months are still twelve rows, and a chart handed rows draws a
     frame rather than the empty state that says nothing reported. */
  if (a.every((v) => v === null) && b.every((v) => v === null)) return [];

  return financialYearMonths().flatMap((label, month) => [
    { period_ts: label, displayPeriods: label, kpi: labelA, sum_value: a[month] },
    { period_ts: label, displayPeriods: label, kpi: labelB, sum_value: b[month] },
  ]);
};

/**
 * Totals a group's rows by whatever splits them.
 * @param {Array<object>} rows - Widget rows from the breakdown request.
 * @param {string} field - Field naming each part, e.g. 'kpi' or 'meter_id'.
 * @returns {Map<string, number>} Part name to its total.
 */
export const breakdownOf = (rows = [], field) => {
  const totals = new Map();
  if (!field) return totals;

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const name = row?.[field] == null ? '' : String(row[field]).trim();
    if (!name) return;

    totals.set(name, (totals.get(name) || 0) + (Number(row.sum_value) || 0));
  });

  return totals;
};

/**
 * Puts the two groups' breakdowns side by side.
 *
 * A part that either group reported is kept, because a borewell that ran in
 * one year and not the other is the finding — dropping it would leave the two
 * columns looking comparable when they are not. Largest first, since that is
 * the order the question is usually asked in.
 *
 * @param {Map<string, number>} a - First group's parts.
 * @param {Map<string, number>} b - Second group's parts.
 * @returns {Array<{category: string, g1: number, g2: number}>} Merged parts.
 */
export const mergeBreakdowns = (a = new Map(), b = new Map()) =>
  Array.from(new Set([...a.keys(), ...b.keys()]))
    .map((category) => ({
      category,
      g1: a.get(category) || 0,
      g2: b.get(category) || 0,
    }))
    .sort((x, y) => y.g1 + y.g2 - (x.g1 + x.g2));

/**
 * The difference between the two groups.
 *
 * A percentage against a base of zero is not a large change, it is no change
 * that can be stated, so the panel is told the two are not comparable rather
 * than being handed an infinity to render.
 *
 * @param {number} from - First group's total.
 * @param {number} to - Second group's total.
 * @returns {{diff: number, percent: number, comparable: boolean}} The change.
 */
export const changeBetween = (from, to) => {
  const first = Number(from) || 0;
  const second = Number(to) || 0;
  const diff = second - first;

  return {
    diff,
    percent: first > 0 ? (diff / first) * 100 : 0,
    comparable: first > 0,
  };
};

/**
 * A straight line through a series, by least squares.
 *
 * Kavana's review: "there is too much variation, you can't make out anything
 * from this data, but at least a trend line will show you if it is really
 * increasing or decreasing." Daily water figures swing enough that the eye
 * cannot tell a drift from noise, which is the question a comparison is being
 * asked to answer.
 *
 * Positions with no reading are skipped rather than read as zero: a meter that
 * was dark for a week would otherwise drag the line down and report a fall
 * that never happened.
 *
 * @param {Array<{x: number, y: number}>} points - Position and value.
 * @returns {{slope: number, intercept: number}|null} The fit, or null when
 *   fewer than two points carry a reading.
 */
export const linearFit = (points = []) => {
  const usable = (Array.isArray(points) ? points : []).filter(
    (p) => Number.isFinite(p?.x) && Number.isFinite(p?.y)
  );
  if (usable.length < 2) return null;

  const n = usable.length;
  const sumX = usable.reduce((t, p) => t + p.x, 0);
  const sumY = usable.reduce((t, p) => t + p.y, 0);
  const sumXY = usable.reduce((t, p) => t + p.x * p.y, 0);
  const sumXX = usable.reduce((t, p) => t + p.x * p.x, 0);

  const denominator = n * sumXX - sumX * sumX;
  if (denominator === 0) return null;

  const slope = (n * sumXY - sumX * sumY) / denominator;

  return { slope, intercept: (sumY - slope * sumX) / n };
};

/**
 * Adds a trend series to widget rows, one point per period.
 *
 * The fit is taken across every series together, because the chart draws one
 * trend for what is plotted rather than one per group — two trend lines on a
 * two-series chart is four lines and reads as noise.
 *
 * @param {Array<object>} rows - Widget rows carrying `period_ts`.
 * @param {string} name - What to call the trend series.
 * @returns {Array<object>} The rows with a trend row per period appended, or
 *   the rows unchanged when there is nothing to fit.
 */
export const withTrend = (rows = [], name = 'Trend') => {
  const source = Array.isArray(rows) ? rows : [];
  const periods = [...new Set(source.map((row) => row.period_ts))];

  const points = periods
    .map((ts, x) => {
      const values = source
        .filter((row) => row.period_ts === ts && row.sum_value !== null)
        .map((row) => Number(row.sum_value) || 0);

      return values.length
        ? { x, y: values.reduce((t, v) => t + v, 0) / values.length }
        : null;
    })
    .filter(Boolean);

  const fit = linearFit(points);
  if (!fit) return source;

  const sample = source[0] || {};

  return [
    ...source,
    ...periods.map((ts, x) => ({
      period_ts: ts,
      displayPeriods:
        source.find((row) => row.period_ts === ts)?.displayPeriods ?? ts,
      kpi: name,
      sum_value: Math.max(0, fit.intercept + fit.slope * x),
      unit: sample.unit,
    })),
  ];
};
